//src/hooks/useOtpFlow.js

import { useState, useEffect, useCallback } from "react";
import { apiUrl } from "../config/apiConfig";

const OTP_DURATION = 300;

const useOtpFlow = () => {
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(OTP_DURATION);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!otpSent || timerSeconds <= 0) return;
    const interval = setInterval(() => setTimerSeconds((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(interval);
  }, [otpSent, timerSeconds]);

  const sendOtp = useCallback(async (msisdn, network) => {
    setError("");
    setSending(true);
    try {
      const operator_name = network.toLowerCase().trim();
      const res = await fetch(apiUrl("/otp/send"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ msisdn, operator_name }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || "Unable to send OTP.");
      setOtpSent(true);
      setTimerSeconds(OTP_DURATION);
      return json;
    } catch (err) {
      setError(err.message || "Failed to send OTP. Please try again.");
      throw err;
    } finally {
      setSending(false);
    }
  }, []);

  const verifyOtp = useCallback(async (msisdn, code) => {
    setError("");
    if (timerSeconds <= 0) {
      const err = new Error("OTP expired. Please resend.");
      setError(err.message);
      throw err;
    }
    setVerifying(true);
    try {
      const res = await fetch(apiUrl("/otp/verify"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ msisdn, otp: code }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || "OTP verification failed.");
      return json;
    } catch (err) {
      setError(err.message || "OTP verification failed. Please try again.");
      throw err;
    } finally {
      setVerifying(false);
    }
  }, [timerSeconds]);

  const reset = useCallback(() => {
    setSending(false);
    setVerifying(false);
    setOtpSent(false);
    setTimerSeconds(OTP_DURATION);
    setError("");
  }, []);

  const formattedTimer = (() => {
    const m = Math.floor(timerSeconds / 60).toString().padStart(1, "0");
    const s = (timerSeconds % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  })();

  return { sending, verifying, otpSent, timerSeconds, formattedTimer, error, sendOtp, verifyOtp, reset, setError };
};

export default useOtpFlow;